import {
    SignalDataEndpoint,
    SignalsEndpoints,
} from "components/Signals/SignalsModels";
import {
    DrilldownBehavior,
    DrilldownCapability,
    DrilldownSource,
    type SelectedDataPoint,
    ViewDataClassification,
} from "./DrilldownTypes";

export const resolveAggregationDrilldownTitle = ({
    selectedDataPoint,
    aggregationLabels = [],
    fallbackTitle,
}: {
    selectedDataPoint: SelectedDataPoint | null;
    aggregationLabels?: string[];
    fallbackTitle?: string;
}): string => {
    const selectedTheme = [
        selectedDataPoint?.x,
        selectedDataPoint?.series,
    ].find(
        (value) =>
            value !== undefined &&
            value !== null &&
            aggregationLabels.includes(String(value)),
    );

    if (selectedTheme !== undefined && selectedTheme !== null) {
        return String(selectedTheme);
    }

    return fallbackTitle ?? "Drilldown";
};

export const resolveViewDataClassification = ({
    configuredClassification,
    canViewDrivers,
}: {
    configuredClassification?: ViewDataClassification;
    canViewDrivers: boolean;
}): ViewDataClassification => {
    if (!canViewDrivers) return "topics";
    return configuredClassification ?? "drivers";
};

export const normalizeEndpointForDrilldown = (
    endpoint?: SignalDataEndpoint | string,
): SignalDataEndpoint | undefined => {
    if (!endpoint) return undefined;
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.PublishedGroupedModelResultsId,
        )
    ) {
        return SignalsEndpoints.additionalReports.PublishedGroupedModelResultsId;
    }
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.PublishedModelResultsOverTimeId,
        )
    ) {
        return SignalsEndpoints.additionalReports
            .PublishedModelResultsOverTimeId;
    }
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.PublishedModelResultsCountId,
        )
    ) {
        return SignalsEndpoints.additionalReports.PublishedModelResultsCountId;
    }
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.ModelResultsOverTimeId,
        )
    ) {
        return SignalsEndpoints.additionalReports.ModelResultsOverTimeId;
    }
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.ModelResultsCountId,
        )
    ) {
        return SignalsEndpoints.additionalReports.ModelResultsCountId;
    }
    return endpoint as SignalDataEndpoint;
};

export const hasSelectedIds = (
    source: DrilldownSource,
): source is DrilldownSource & {
    selectedIds: NonNullable<DrilldownSource["selectedIds"]>;
} => !!source.selectedIds;

export const hasTopics = (
    source: DrilldownSource,
): source is DrilldownSource & {
    topics: NonNullable<DrilldownSource["topics"]>;
} => Array.isArray(source.topics);

export const hasThemes = (
    source: DrilldownSource,
): source is DrilldownSource & {
    themes: NonNullable<DrilldownSource["themes"]>;
} => Array.isArray(source.themes);

export const hasParsedVizOptions = (
    source: DrilldownSource,
): source is DrilldownSource & {
    parsedVizOptions: NonNullable<DrilldownSource["parsedVizOptions"]>;
} => !!source.parsedVizOptions;

export const hasData = (
    source: DrilldownSource,
): source is DrilldownSource & {
    data: NonNullable<DrilldownSource["data"]>;
} => Array.isArray(source.data);

export const hasTemporalGrain = (
    source: DrilldownSource,
): source is DrilldownSource & {
    temporalGrain: NonNullable<DrilldownSource["temporalGrain"]>;
} => !!source.temporalGrain;

export const hasCapability = (
    source: DrilldownSource,
    capability: DrilldownCapability,
) => {
    if (capability.startsWith("selectedIds.")) {
        if (!hasSelectedIds(source)) return false;
        const key = capability.split(".")[1];
        const value = source.selectedIds[key];
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== null;
    }
    switch (capability) {
        case "topics":
            return hasTopics(source);
        case "themes":
            return hasThemes(source);
        case "parsedVizOptions":
            return hasParsedVizOptions(source);
        case "data":
            return hasData(source);
        case "temporalGrain":
            return hasTemporalGrain(source);
        default:
            return true;
    }
};

export const findMissingCapabilities = (
    strategy: DrilldownBehavior | undefined,
    source?: DrilldownSource,
) => {
    if (!strategy?.needs || !source) return [];
    return strategy.needs.filter((need) => !hasCapability(source, need));
};
